/**
 * Lightweight analytics tracking utility.
 * Fires custom events that can be captured by any analytics provider
 * (Google Analytics, Plausible, etc.) and logs to console in dev.
 */

export type EventName =
  | "organization_partnership_view"
  | "organization_partnership_cta"
  | "organization_inquiry_started"
  | "organization_inquiry_submitted"
  | "rewards_preview_signup_click"
  | "ruben_lp_view"
  | "ruben_hero_lp_view"
  | "ruben_hero_cta_click"
  | "phone_prompt_shown"
  | "profile_question_shown"
  | "profile_question_answered"
  | "profile_question_skipped"
  | "hba_webinar_card_shown"
  | "hba_webinar_card_saved"
  | "hba_webinar_card_dismissed"
  | "credit_first_click"
  | "partner_page_view"
  | "partner_page_cta"
  | "employer_access_requested"
  | "peo_code_verified"
  | "peo_signup_completed"
  | "employer_access_hr_note_copied"
  | "phone_prompt_saved"
  | "phone_prompt_dismissed"
  | "ruben_hero_results_view"
  | "ruben_hero_stack_shown"
  | "ruben_hero_smart_start_shown"
  | "ruben_hero_smart_start_click"
  | "ruben_hero_agent_click"
  | "ruben_hero_preapproval_click"

  | "ruben_lp_cta_click"
  | "hero_stack_lp_view"
  | "hero_stack_lp_cta_click"
  | "hero_stack_lp_hero_type"
  | "hero_stack_lp_estimator_use"
  | "hero_stack_thankyou_view"
  | "hero_stack_thankyou_choice"

  | "hero_stack_lp_zip_entered"
  | "hero_stack_signup_screen_viewed"
  | "hero_stack_signup_completed"
  | "hero_stack_auto_run_completed"
  | "hero_stack_layer_claimed"

  | "signup_started"
  | "signup_completed"
  | "login"
  | "onboarding_completed"
  | "vip_inquiry_clicked"
  | "tool_used"
  | "guide_downloaded"
  | "newsletter_subscribed"
  | "content_gate_submitted"
  | "credit_quiz_completed"
  | "grant_finder_used"
  | "grant_stack_run"
  | "grant_stack_saved"
  | "stack_share_click"
  | "stack_share_copy"
  | "lender_match_returned"
  | "lender_intro_requested"
  | "lender_shortlist_impression"
  | "lender_calendly_click"
  | "lender_coverage_notify_me"

  | "employer_added"
  | "calculator_used"
  | "expert_booked"
  | "employer_referral_submitted"
  | "blog_read"
  | "webinar_rsvp"
  | "track_selected"
  | "quote_guide_cta_click"
  | "assistance_guide_cta_click"
  | "assistance_guide_section_view"
  | "dpa_cta_click"
  | "dpa_faq_expand"
  | "dpa_guide_section_view"
  | "dpa_calculator_run"
  | "foreclosure_guide_cta_click"
  | "foreclosure_guide_section_view"
  | "option_compare_expand"
  | "counselor_help_click"
  | "faq_expand"
  | "loan_estimate_checklist_start"
  | "worksheet_row_complete"
  | "negotiation_script_copy"
  | "checklist_download"
  | "checklist_section_view"
  | "compare_table_interaction"
  | "lender_cta_click"
  | "mortgage_credit_guide_cta_click"
  | "timeline_expand"
  | "warning_view"
  | "guide_cta_click"
  | "address_check_click"
  | "faq_open"
  | "compare_row_expand"
  | "grant_guide_view"
  | "program_card_expand"
  | "state_selector_use"
  | "matching_tool_start"
  | "consult_request_submit"
  | "timeline_guide_view"
  | "timeline_phase_click"
  | "checklist_expand"
  | "lead_form_start"
  | "lead_form_submit"
  | "readiness_guide_view"
  | "readiness_calc_start"
  | "readiness_score_calculated"
  | "readiness_result_cta_click"
  | "relocation_guide_view"
  | "guide_strategy_select"
  | "relocation_cta_click"
  | "guide_view"
  | "option_table_interaction"
  | "faq_toggle"
  | "expert_review_expand"
  | "inheritance_guide_cta_click"
  | "inheritance_checklist_expand"
  | "revenue_explainer_view"
  | "disclosure_expand"
  | "expert_match_cta_click"
  | "affordability_calc_start"
  | "affordability_calc_complete"
  | "affordability_cta_click"
  | "trial_run_expand"
  | "checklist_cta_click"
  | "tool_link_click"
  | "cash_to_close_cta_click"
  | "cash_to_close_faq_expand"
  | "toc_jump"
  | "home_value_estimator_start"
  | "home_value_estimator_complete"
  | "cma_cta_click"
  | "appraisal_details_expand"
  | "counselor_search_cta_click"
  | "hotline_click"
  | "external_resource_click"
  | "resource_card_click"
  | "local_help_cta_click"
  | "compare_table_sort"
  | "faq_cta_click"
  | "apply_start"
  | "readiness_tool_click"
  | "comparison_table_interaction"
  | "cta_next_step"
  | "dpa_calc_start"
  | "dpa_calc_complete"
  | "dpa_result_view"
  | "occupation_selected"
  | "state_selector_used"
  | "lender_guidance_cta_click"
  | "authority_card_expand"
  | "dpa_guide_cta_click"
  | "dpa_faq_open"
  | "dpa_table_expand"
  | "assistance_check_click"
  | "status_glossary_expand"
  | "document_checklist_save"
  | "checklist_print"
  | "fee_term_expand"
  | "escalation_template_copy"
  | "contact_cta_click"
  | "checklist_interaction"
  | "cta_click"
  | "hub_cta_click"
  | "state_example_expand"
  | "fair_quote_checker_start"
  | "fair_quote_checker_complete"
  | "benchmark_table_expand"
  | "student_loan_dti_estimate_start"
  | "student_loan_dti_estimate_complete"
  | "program_rule_expand"
  | "mortgage_help_cta_click"
  | "budget_tool_start"
  | "budget_tool_complete"
  | "generate_lead"
  | "download_checklist"
  | "debt_vs_downpayment_calc_start"
  | "debt_vs_downpayment_calc_complete"
  | "recommendation_cta_click"
  | "mortgage_compare_start"
  | "mortgage_compare_complete"
  | "rate_edit"
  | "goal_select"
  | "invest_toggle"
  | "mortgage_decision_calc_start"
  | "mortgage_decision_calc_complete"
  | "decision_path_select"
  | "worksheet_cta_click"
  | "pmi_estimator_start"
  | "pmi_estimator_complete"
  | "request_template_copy"
  | "checklist_complete"
  | "dti_calculator_use"
  | "commission_calculator_use"
  | "state_example_select"
  | "calculator_start"
  | "calculator_complete"
  | "tool_cta_click"
  | "rent_sell_calc_start"
  | "rent_sell_calc_complete";




interface EventProperties {
  [key: string]: string | number | boolean | undefined;
}

export const trackEvent = (name: EventName, properties?: EventProperties) => {
  // Log in development
  if (import.meta.env.DEV) {
    console.log(`📊 [Analytics] ${name}`, properties || "");
  }

  // Dispatch a custom DOM event for any external analytics script to capture
  try {
    window.dispatchEvent(
      new CustomEvent("ha_analytics", {
        detail: { event: name, properties, timestamp: Date.now() },
      })
    );
  } catch {
    // silently fail
  }

  // If Google Analytics gtag is loaded
  if (typeof window !== "undefined" && (window as any).gtag) {
    (window as any).gtag("event", name, properties);
  }
};

/**
 * Same dispatch as trackEvent, without the EventName union constraint.
 * Escape hatch for per-post GA4 event names sourced from
 * blog_posts.geckocheck_event_names (each GeckoCheck content project can
 * request its own literal event name) — using this instead of trackEvent
 * avoids adding hundreds of one-off literal names to the shared union.
 * Prefer trackEvent for anything with a fixed, known name.
 */
export const trackDynamicEvent = (name: string, properties?: EventProperties) => {
  trackEvent(name as EventName, properties);
};
